import { useQuery } from "@tanstack/react-query";
import { TreeDataNode } from "antd";
import { useCallback, useMemo } from "react";

import { msGraphHelper } from "../../../../helper/ms-graph-helper";
import { useSearchParams } from "../../../../hook/use-search-params";
import { DocumentApiV2 } from "../../../../services/document/document-v2.service";
import { MSDriveItem } from "../../../../types/ms-graph";
import { compactData, toFlat, toTree } from "../../utils/document";
const lookup: Record<string, MSDriveItem> = {};

export const usePublicDocDirectory = () => {
  const { searchParams } = useSearchParams();

  const status = searchParams.get("status");

  const query = useQuery({
    queryKey: ["publish-folders"],
    queryFn: async () => {
      const response = await DocumentApiV2.getDocumentsDirectory(true);

      return response;
    },
  });

  const queryNew = useQuery({
    queryKey: ["publish-folders-new", status],
    queryFn: () =>
      status === "publish" ? DocumentApiV2.getDocumentsMgmtDirectoryNew() : DocumentApiV2.getDocumentsTreeNew(),
  });

  const sortByName = searchParams.get("sort") === "abc";

  const compactedData = useMemo(() => compactData((query?.data ?? []) as any), [query.data]);

  const compactedNewData = useMemo(() => toFlat(queryNew.data?.data || []), [queryNew.data?.data]);

  compactedData.forEach((item) => (lookup[item.id] = { ...item, children: [] }));

  const hashedData = useMemo(() => {
    const map = new Map();
    compactedData.forEach((item) => map.set(item.id, item));

    return map;
  }, [compactedData]);

  const hashedNewData = useMemo(() => {
    const map = new Map();
    compactedNewData.forEach((item: any) => map.set(item.key, item));

    return map;
  }, [compactedNewData]);

  const getSortData = useCallback(
    (sortByName: boolean) => (a: any, b: any) => {
      if (sortByName) {
        return a.name.localeCompare(b.name); // A → Z
      }

      if (a.docPosition !== undefined && b.docPosition !== undefined) {
        return a.docPosition - b.docPosition;
      }

      if (a.docPosition !== undefined) return -1;
      if (b.docPosition !== undefined) return 1;

      return a.name.localeCompare(b.name);
    },
    [],
  );

  const treeData = useMemo(() => {
    const sorted = msGraphHelper.sortNestedTree(
      msGraphHelper.buildTreeFromDriveItem(compactedData),
      getSortData(sortByName),
    );
    return msGraphHelper.mapNestedTree<TreeDataNode>(sorted, (item) => ({ title: item.name, key: item.id }));
  }, [compactedData, getSortData, sortByName]);

  const treeDataNew = useMemo(() => toTree(queryNew.data?.data || []), [queryNew.data?.data]);

  return {
    ...query,
    data: compactedData,
    treeData: treeDataNew,
    treeDataOld: treeData,
    hashedData,
    isLoading: queryNew.isLoading,
    isError: queryNew.isError,
  };
};
